import { useQuery } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'

import { fetchUnreadCount } from '@/features/notifications/api/api'
import { NOTIFICATIONS_POLL_INTERVAL_MS } from '@/config/constants'
import { requireAuthToken } from '@/lib/api'
import { notificationKeys } from '@/lib/queryKeys'

/**
 * Drives the bell badge.
 *
 * Polls on NOTIFICATIONS_POLL_INTERVAL_MS and refetches on focus.
 * `refetchOnWindowFocus: true` is a deliberate divergence from `useOrgMetrics`,
 * which sets it false — a badge that is stale when you tab back is the whole
 * failure this feature exists to avoid.
 *
 * Errors are swallowed by the caller rather than surfaced: if relay lags a hoku
 * deploy, this request fails every poll, and a toast per poll is worse than no
 * badge. `retry: false` keeps a persistent outage from tripling that rate.
 */
export function useUnreadCountQuery() {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const orgId = organization?.id ?? ''

  return useQuery({
    queryKey: notificationKeys.unreadCount(orgId),
    queryFn: async ({ signal }) => {
      const token = await requireAuthToken(getToken)
      return fetchUnreadCount({ token, signal })
    },
    enabled: !!orgId,
    staleTime: 0,
    refetchInterval: NOTIFICATIONS_POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    retry: false,
  })
}
